import React, { useState, useEffect, useRef } from 'react';
import Loading from '../Common/Loading';

interface QuestionStat {
    perc: number;
    total: number;
    display_text: string;
}

interface LessonRow {
    lesson_code: string;
    lesson_name: string;
    avg_score: number;
    questions: Record<string, QuestionStat>;
}

interface TestGroup {
    test_code: string;
    test_name: string;
    test_overall_avg: number;
    lessons: LessonRow[];
}

interface FlatRow {
    type: 'lesson' | 'avg';
    rowNum: number;
    testGroup: TestGroup;
    lessonRow?: LessonRow;
    lIdx?: number;
}

const EL_TestSummary: React.FC = () => {
    const API_BASE = process.env.REACT_APP_DOCUMENT_LINK || 'https://staging.sysadmin.elearnoncloud.com/';

    const [country, setCountry] = useState('Australia');
    const [dics, setDics] = useState<string[]>([]);
    const [selectedDic, setSelectedDic] = useState('--Select--');
    const [threshold, setThreshold] = useState(70);
    const [famWise, setFamWise] = useState(true);
    const [gridData, setGridData] = useState<TestGroup[]>([]);
    const [totalTests, setTotalTests] = useState<number>(0);
    const [loading, setLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [selectedRowCode, setSelectedRowCode] = useState<string | null>(null);
    // Measured height of first sticky header row — used to offset second sticky row
    const [firstRowHeight, setFirstRowHeight] = useState<number>(0);

    // const firstHeaderRowRef = useRef<HTMLTableRowElement>(null);

    const firstHeaderRowRef = useRef<HTMLTableRowElement>(null);
    const requestIdRef = useRef(0);

    const MAX_QUESTIONS = 100;
    const columns = Array.from({ length: MAX_QUESTIONS }, (_, i) => String(i + 1).padStart(3, '0'));

    useEffect(() => {
        document.body.style.backgroundColor = '#fff';
    }, []);

    // Measure first header row height after render so thSub top offset is exact
    useEffect(() => {
        if (firstHeaderRowRef.current) {
            setFirstRowHeight(firstHeaderRowRef.current.getBoundingClientRect().height);
        }
    }, [gridData, hasSearched]);

    const colors = {
        headBg: '#c7ddfd', headText: '#2859ac', border: '#80a0ff',
        rowOdd: '#f4faff', rowEven: '#fff', rowSelected: 'orange',
        failBg: 'red', failText: 'white', avgRowBg: '#c1bbef', avgRowText: '#2859ac'
    };

    // Header th — full border, sticky row 1
    const thBase: React.CSSProperties = {
        backgroundColor: colors.headBg,
        color: colors.headText,
        border: `1px solid ${colors.border}`,
        padding: '4px 6px',
        textAlign: 'center',
        position: 'sticky',
        top: 0,
        zIndex: 3,
        whiteSpace: 'nowrap'
    };

    // Sub-header th (question numbers) — sticky row 2, top = measured first row height
    const thSubBase: React.CSSProperties = {
        backgroundColor: colors.headBg,
        color: colors.headText,
        border: `1px solid ${colors.border}`,
        padding: '4px 6px',
        textAlign: 'center',
        position: 'sticky',
        top: firstRowHeight > 0 ? `${firstRowHeight}px` : '31px',
        zIndex: 3,
        whiteSpace: 'nowrap'
    };

    // Body td — right border only, 8px padding
    const tdBase: React.CSSProperties = {
        borderRight: `1px solid ${colors.border}`,
        borderLeft: 'none',
        borderTop: 'none',
        borderBottom: 'none',
        padding: '8px',
        textAlign: 'center',
        whiteSpace: 'nowrap',
        fontSize: '13px'
    };

    useEffect(() => {
        const fetchDics = async () => {
            setLoading(true);
            try {
                const res = await fetch(`${API_BASE}api/q_perf_get_dics?country=${country}`);
                const json = await res.json();
                setDics(json.dics || []);
                setSelectedDic('--Select--');
                setGridData([]);
                setTotalTests(0);
                setHasSearched(false);
            } catch (err) { console.error(err); }
            finally { setLoading(false); }
        };
        fetchDics();
    }, [country]);

    const handleGo = async () => {
        if (selectedDic === '--Select--') {
            alert("Please select a Dicfile.");
            return;
        }

        // Create a unique ID for this request
        const requestId = ++requestIdRef.current;

        setLoading(true);
        setHasSearched(true);
        setSelectedRowCode(null);

        // Clear previous DicFile data immediately
        setGridData([]);
        setTotalTests(0);

        const currentDic = selectedDic;
        const currentCountry = country;

        try {
            const res = await fetch(`${API_BASE}api/ts_perf_get_data`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    country: currentCountry,
                    dic_code: currentDic
                })
            });

            const json = await res.json();

            // Ignore response if a newer GO request has already started
            if (requestId !== requestIdRef.current) {
                return;
            }

            if (json.error) {
                alert(json.error);
                setGridData([]);
                setTotalTests(0);
                return;
            }

            const results = json.results || [];

            // Only update UI with the latest request
            setGridData(results);
            setTotalTests(json.total_tests ?? results.length);

        } catch (err) {

            // Ignore errors from an old request
            if (requestId !== requestIdRef.current) {
                return;
            }

            console.error(err);
            setGridData([]);
            setTotalTests(0);

        } finally {

            // Only latest request controls loading state
            if (requestId === requestIdRef.current) {
                setLoading(false);
            }
        }
    };
    const handleClose = () => { window.location.href = "/SysAdmin"; };

    const handleCellClick = (
        e: React.MouseEvent,
        lessonCode: string,
        qnNo: string,
        testCode: string
    ) => {
        e.stopPropagation();

        setSelectedRowCode(lessonCode);

        const w = 1100;
        const h = 800;
        const left = (window.screen.width / 2) - (w / 2);
        const top = (window.screen.height / 2) - (h / 2);

        const url = famWise
            ? `/SUPPORT/EL_QuestionInsights?DICCode=${selectedDic}&Qnno=${qnNo}&Title=${lessonCode}&TestCode=${testCode}&Country=${country}&from=TestSummary`
            : `/LessonBrowser/QuestionViewer?Qnno=${qnNo}&Title=${lessonCode}&TestCode=${testCode}&Country=${country}&from=TestSummary`;

        window.open(
            url,
            "_blank",
            `width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes`
        );
    };
    // Build flat rows with sequential numbering
    const flatRows: FlatRow[] = [];
    let rowCounter = 0;
    gridData.forEach(testGroup => {
        testGroup.lessons.forEach((lessonRow, lIdx) => {
            rowCounter++;
            flatRows.push({ type: 'lesson', rowNum: rowCounter, testGroup, lessonRow, lIdx });
        });
        rowCounter++;
        flatRows.push({ type: 'avg', rowNum: rowCounter, testGroup });
    });

    return (
        <div style={{ fontFamily: 'Arial', fontSize: '13pt', margin: 0, overflowX: 'hidden' }}>
            {loading && <Loading />}

            {/* ── Teal title bar ── */}
            <div style={{ backgroundColor: 'teal', color: 'white', textAlign: 'center', fontSize: '16pt', padding: '10px' }}>
                TEST SUMMARY ANALYSIS
            </div>

            {/* ── Filter bar ── */}
            <div style={{ padding: '10px', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '10px', backgroundColor: '#f9f9f9', borderBottom: '1px solid #ccc' }}>
                <span>Country :</span>
                <select style={{ fontFamily: 'Arial', fontSize: '14pt', color: 'blue', padding: '2px' }} value={country} onChange={(e) => setCountry(e.target.value)}>
                    <option value="Australia">Australia</option>
                    <option value="Canada">Canada</option>
                </select>

                <span style={{ marginLeft: '6px' }}>Dicfile :</span>
                <select style={{ fontFamily: 'Arial', fontSize: '14pt', color: 'blue', padding: '2px', width: '200px' }} value={selectedDic} onChange={(e) => setSelectedDic(e.target.value)}>
                    <option>--Select--</option>
                    {dics.map(d => <option key={d} value={d}>{d}</option>)}
                </select>

                <select style={{ fontFamily: 'Arial', fontSize: '14pt', color: 'blue', height: '28px', marginLeft: '6px' }} value={threshold} onChange={(e) => setThreshold(Number(e.target.value))}>
                    {[100, 90, 80, 70, 60, 50, 40, 30, 20, 10].map(v => <option key={v} value={v}>{v}</option>)}
                </select>

                <button style={{ fontFamily: 'Arial', fontSize: '12pt', cursor: 'pointer', padding: '4px 15px' }} onClick={handleGo}>GO</button>

                <label style={{ fontSize: '14px', cursor: 'pointer', marginLeft: '6px' }}>
                    <input type="checkbox" checked={famWise} onChange={(e) => setFamWise(e.target.checked)} />
                    {' '}Family-wise Wrong Answers
                </label>

                <button style={{ fontFamily: 'Arial', fontSize: '14pt', cursor: 'pointer', padding: '4px 15px', marginLeft: 'auto' }} onClick={handleClose}>CLOSE</button>
            </div>

            {/* ── "Question Nos And Marks / Total Tests:N" ──
                Separate centered header with same blue bg as table header, exactly like VB */}
            {hasSearched && !loading && gridData.length > 0 && (
                <div style={{
                    backgroundColor: colors.headBg,
                    color: colors.headText,
                    textAlign: 'center',
                    padding: '5px 0',
                    fontFamily: 'Arial',
                    fontSize: '13px',
                    borderBottom: `1px solid ${colors.border}`
                }}>
                    <div>Question Nos And Marks</div>
                    <div style={{ fontWeight: 'bold' }}>Total Tests:{totalTests}</div>
                </div>
            )}

            {/* ── Grid ── */}
            <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '900px', width: '100%' }}>
                {hasSearched && gridData.length === 0 && !loading ? (
                    <div style={{ padding: '20px', textAlign: 'center' }}>No data available for this dictionary.</div>
                ) : (
                    <table style={{ borderCollapse: 'collapse', width: 'max-content', fontSize: '13px', fontFamily: 'Arial' }}>
                        <thead>
                            {/* ── Row 1: column labels (rowSpan=2 for fixed cols) + spanning "Question Nos And Marks" ── */}
                            <tr ref={firstHeaderRowRef}>
                                <th style={{ ...thBase, minWidth: '30px' }} rowSpan={2}>#</th>
                                <th style={{ ...thBase, textAlign: 'left', minWidth: '80px' }} rowSpan={2}>TestCode</th>
                                <th style={{ ...thBase, textAlign: 'left', minWidth: '160px' }} rowSpan={2}>TestName</th>
                                <th style={{ ...thBase, textAlign: 'left', minWidth: '100px' }} rowSpan={2}>LessonCode</th>
                                <th style={{ ...thBase, textAlign: 'left', minWidth: '220px' }} rowSpan={2}>LessonName</th>
                                <th style={{ ...thBase, minWidth: '80px' }} rowSpan={2}>Av lsnScore</th>
                                {/* Spanning cell — just the label, no height tricks needed */}
                                <th colSpan={MAX_QUESTIONS} style={{ ...thBase, textAlign: 'center' }}>
                                    Question Nos And Marks
                                </th>
                            </tr>
                            {/* ── Row 2: question number sub-headers ── */}
                            <tr>
                                {columns.map(col => (
                                    <th key={col} style={{ ...thSubBase, minWidth: '48px' }}>
                                        {parseInt(col)}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {flatRows.map((fr) => {
                                if (fr.type === 'avg') {
                                    return (
                                        <tr key={`avg-${fr.testGroup.test_code}`} style={{ backgroundColor: colors.avgRowBg, padding: "10px" }}>
                                            <td style={{ ...tdBase, color: colors.avgRowText, fontWeight: 'bold' }}>{fr.rowNum}</td>
                                            <td style={tdBase}></td>
                                            <td style={tdBase}></td>
                                            <td style={tdBase}></td>
                                            <td style={{ ...tdBase, textAlign: 'left', fontWeight: 'bold', color: colors.avgRowText }}>Average Test Score</td>
                                            <td style={{ ...tdBase, fontWeight: 'bold', color: colors.avgRowText }}>{fr.testGroup.test_overall_avg}</td>
                                            {columns.map(col => <td key={col} style={tdBase}></td>)}
                                        </tr>
                                    );
                                }

                                const row = fr.lessonRow!;
                                const isSelected = selectedRowCode === row.lesson_code;
                                const rowBg = isSelected ? colors.rowSelected : (fr.lIdx! % 2 === 0 ? colors.rowEven : colors.rowOdd);

                                return (
                                    <tr key={row.lesson_code} style={{ backgroundColor: rowBg, cursor: 'pointer' }} onClick={() => setSelectedRowCode(row.lesson_code)}>
                                        <td style={{ ...tdBase, color: '#555' }}>{fr.rowNum}</td>
                                        <td style={{ ...tdBase, textAlign: 'left', fontWeight: 'bold' }}>{fr.testGroup.test_code}</td>
                                        <td style={{ ...tdBase, textAlign: 'left' }}>{fr.testGroup.test_name}</td>
                                        <td style={{ ...tdBase, textAlign: 'left', fontWeight: 'bold' }}>{row.lesson_code}</td>
                                        <td style={{ ...tdBase, textAlign: 'left' }}>{row.lesson_name}</td>
                                        <td style={{ ...tdBase, fontWeight: 'bold' }}>{row.avg_score}</td>
                                        {columns.map(col => {
                                            const qnStat = row.questions[col];
                                            const isFail = qnStat && qnStat.perc < threshold;
                                            return (
                                                <td
                                                    key={col}
                                                    style={{
                                                        ...tdBase,
                                                        backgroundColor: isFail ? colors.failBg : 'transparent',
                                                        color: isFail ? colors.failText : '#000',
                                                        cursor: qnStat ? 'pointer' : 'default'
                                                    }}
                                                    onClick={(e) => { if (qnStat) handleCellClick(e, row.lesson_code, col, fr.testGroup.test_code); }}
                                                >
                                                    {qnStat ? qnStat.display_text : ''}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default EL_TestSummary;